import {
    Table,
    TableBody,
    TableCell,
    TableRow,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import i18next from "i18next";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as constants from "../../../constants";
import { ColorModeContext } from "../../../shared/Theme/AppContext";
import { setLoginDetails } from "../../../store/features/auth/authSlice";
import { RootState } from "../../../store/store";
import { saveUserProfile } from "../../fiscalUpgrade/services/Settings/ProfileService";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer } from "../../fiscalUpgrade/StyledComponents/Container";
import { getClientDetailsByClientName } from "../../fiscalUpgrade/Utils/ClientSettingsTable";
import { notifyUser } from "../../fiscalUpgrade/Utils/notifyUser";
import Languages from "../Languages";
import TimeZoneSelector from "../TimeZoneSelector";

interface CustomizeHeaderProps {
    view: string;
    setView: React.Dispatch<React.SetStateAction<string>>;
}

const ProfileSettings: React.FC<CustomizeHeaderProps> = ({ view, setView }) => {

    const { mode, toggleColorMode } = React.useContext(ColorModeContext);
    const profileDetails = useSelector((state: RootState) => state.profileSettings);
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const dispatch = useDispatch();
    const userDetails = useSelector((state: RootState) => state.auth);
    const ClientDetails = getClientDetailsByClientName(userDetails?.clientVo?.name);

    useEffect(() => {
        setView(userDetails.menuPosition);
        i18next.changeLanguage(userDetails?.language);
    }, [userDetails]);


    const handleToggleChange = (event: React.MouseEvent<HTMLElement>, newView: string) => {
        if (newView) {
            saveProfileMenu(newView);
            setView(newView);
        }
    };

    async function saveProfileMenu(value: any) {
        try {
            const response = await saveUserProfile(userDetails.id, { menuPosition: value });
            const updatedUser = response.data.data[0];
            if (response.status == constants.STATUS_CODES.SUCCESS) {
                notifyUser(response?.data?.message, 'success', 'Menu');
            } else {
                notifyUser(response?.data?.message, 'error', 'Menu');
            }
            dispatch(setLoginDetails({
                ...updatedUser,
                menuPosition: value
            }));
        } catch (error) {
            notifyUser('Unexpected error occurred', 'error', 'Menu');
            console.warn(error);
        }
    }

    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{translate('profileSettings')} </SectionTitle>
                    <Table sx={{ borderCollapse: "collapse", width: "100%" }}>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold", textAlign: "right", width: "20%", border: "none" }}>
                                    {translate('menu')} :
                                </TableCell>

                                <TableCell sx={{ textAlign: "left", border: "none", pl: 0 }}>
                                    <ToggleButtonGroup
                                        value={view}
                                        exclusive
                                        onChange={handleToggleChange}
                                        size="small"
                                        aria-label="view toggle"
                                    >
                                        <ToggleButton
                                            value="Top"
                                            aria-label="Top View"
                                            sx={{
                                                paddingX: 4,
                                                textTransform: 'none',
                                                color: view === 'Top' ? 'white' : 'black',
                                                backgroundColor: view === 'Top' ? 'black' : 'transparent',
                                                fontWeight: view === 'Top' ? 'bold' : 'normal',
                                                '&:hover': {
                                                    backgroundColor: view === 'Top' ? 'black' : '#f0f0f0',
                                                },
                                            }}
                                        >
                                            {translate('horizontal')}
                                        </ToggleButton>
                                        <ToggleButton
                                            value="Left"
                                            aria-label="Left View"
                                            sx={{
                                                paddingX: 4,
                                                textTransform: 'none',
                                                color: view === 'Left' ? 'white' : 'black',
                                                backgroundColor: view === 'Left' ? 'black' : 'transparent',
                                                fontWeight: view === 'Left' ? 'bold' : 'normal',
                                                '&:hover': {
                                                    backgroundColor: view === 'Left' ? 'black' : '#f0f0f0',
                                                },
                                            }}
                                        >
                                            {translate('vertical')}
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold", textAlign: "right", width: "20%", border: "none" }}>
                                    {translate('language')} :
                                </TableCell>
                                <Languages />
                            </TableRow>

                            {ClientDetails?.appSettings.timeZoneSettings === constants.IS_ACTIVE_YES && <TableRow>
                                <TableCell sx={{ fontWeight: "bold", textAlign: "right", width: "20%", border: "none" }}>
                                    {translate('timeZone')} :
                                </TableCell>
                                <TimeZoneSelector />
                            </TableRow>}

                        </TableBody>
                    </Table>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>

    );
}

export default ProfileSettings;
